import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { provideRouter } from "@angular/router";
import { provideState, provideStore } from "@ngrx/store";
import { userFeature } from "./store/user.era";
import { uiFeature } from "./store/ui.era";
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore'
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { getAnalytics, provideAnalytics } from "@angular/fire/analytics";
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFunctions } from '@angular/fire/functions'; 
import { connectFunctionsEmulator, provideFunctions } from '@angular/fire/functions';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'; // This will soon be deprecated in favor of native css with animate.enter properties used
import { provideQuillConfig } from 'ngx-quill/config';
import {
  provideRouterStore,
  routerReducer,
} from '@ngrx/router-store';
import { connectAuthEmulator, getAuth, provideAuth } from "@angular/fire/auth";
import { environment } from "../environments/environment";
import { eventsFeature } from "./store/events.era";
import { accountsFeature } from "./store/accounts.era";
import { PermissionsEffects, permissionsFeature } from "./store/permissions.era";
import { settingsFeature } from "./store/settings.era";
import { eventPreferenceFeature } from "./store/event.preferences.era";
import { contactsFeature } from "./store/contacts.era";
import { filtersFeature } from "./store/filters.era";
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from "@angular/material/core";
import { provideEffects } from "@ngrx/effects";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { tokenInterceptor } from "./services/guards/token.interceptor";
import { programsFeature } from "./store/programs.era";
import { exhibitorsFeature } from "./store/exhibitors.era";
import { onsiteFeatures } from "./store/onsite.era";
import { assetsFeature } from "./store/assets.era";
import { performersFeature } from "./store/performers.era";
import { exhibitorStandsFeature } from "./store/exhibitor.stands.era";
import { shareFeature } from "./store/share.era";
import { tasksFeature } from "./store/tasks.era";
import { workforceFeature } from "./store/workforce.era";
import { sponsorsFeature } from "./store/sponsors.era";
import { resourcesFeature } from "./store/resources.era";
import { attendeesFeature } from "./store/attendees.era";
import { appsFeature } from "./store/app.era";
import { backupsFeature } from "./store/backups.era";
import { budgetFeature } from "./store/budget.era";
import { cacheFeature } from "./store/cache.era";
import { calendarFeature } from "./store/calendars.era";
import { entitiesFeature } from "./store/entity.era";
import { informableFeature } from "./store/informables.era";
import { noticesFeature } from "./store/notices.era";
import { paymentFeature } from "./store/payments.era";
import { routes } from "./share.app.routing";

export const shareAppConfig: ApplicationConfig = {
  providers: [
    // Add global providers here
    provideRouter(routes),
    provideState(accountsFeature),
    provideState(appsFeature),
    provideState(assetsFeature),
    provideState(attendeesFeature),
    provideState(backupsFeature),
    provideState(budgetFeature),
    provideState(cacheFeature),
    provideState(calendarFeature),
    provideState(contactsFeature),
    provideState(entitiesFeature),
    provideState(eventsFeature),
    provideState(eventPreferenceFeature),
    provideState(exhibitorsFeature),
    provideState(exhibitorStandsFeature),
    provideState(filtersFeature),
    provideState(informableFeature),
    provideState(noticesFeature),
    provideState(onsiteFeatures),
    provideState(paymentFeature),
    provideState(performersFeature),
    provideState(permissionsFeature),
    provideState(programsFeature),
    provideState(resourcesFeature),
    provideState(settingsFeature),
    provideState(shareFeature),
    provideState(sponsorsFeature),
    provideState(tasksFeature),
    provideState(uiFeature),
    provideState(userFeature),
    provideState(workforceFeature),
    provideQuillConfig({
      modules: {
        syntax: false,
        toolbar: { container: [['bold', 'italic', 'underline'], [{ 'list': 'ordered' }, { 'list': 'bullet' }], [{ align: '' }, { align: 'justify' }, { align: 'center' }, { align: 'right' }]]}
      }
    }),
    provideState({ name: 'router', reducer: routerReducer }),
    provideStore({reducer: routerReducer}),
    provideRouterStore(),
    provideAnimationsAsync(),
    provideEffects([PermissionsEffects]),
    provideNativeDateAdapter(), {provide: MAT_DATE_LOCALE, useValue: 'en-AU'},
    provideHttpClient(withInterceptors([tokenInterceptor])),// importProvidersFrom(HttpClientModule)
    provideFirebaseApp(() => {
      console.log('+*+ provideFirebaseApp hit!');
      try {
        const app = initializeApp(environment.firebase);
        console.log('Firebase App initialized successfully:  App Name:', app.name);
        return app;
      } catch (error) {
        console.error('ERROR during Firebase App initialization:', error);
        throw error; // Re-throw to propagate the error
      }
    }),
    provideAuth(() => {
            const auth = getAuth();
            if (environment.useEmulator) connectAuthEmulator(auth, "http://localhost:9099");
            return auth;
        }),
    provideFirestore(() => {
console.log('+*+ provideFirestore hit!');
      const firestore = getFirestore();
      if (environment.useEmulator) { // Use environment.useEmulator for debug builds
        connectFirestoreEmulator(firestore, '127.0.0.1', 8080); // Connect *this* firestore instance to emulator (note common port 8080)
      }
      return firestore;
    }),
    provideFunctions(() => {
console.log('+*+ provideFunctions hit!');
      const functions = getFunctions();
      if (environment.useEmulator) { // Use environment.useEmulator for debug builds
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      console.log('provideFunctions hit!')
      return functions;
    }),
    provideAnalytics(() => getAnalytics()), // Initialize Analytics if needed
    provideStorage(() => {
console.log('+*+ provideStorage hit!');
      const storage = getStorage();
      if (environment.useEmulator) { // Use environment.useEmulator for debug builds
        connectStorageEmulator(storage, '127.0.0.1', 9199); // Connect *this* storage instance to emulator
      }
      return storage;
    }),
    provideZoneChangeDetection(),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production })
  ]
};